import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Loader2, DollarSign, CreditCard,
  LayoutDashboard, CircleDollarSign, ArrowLeftRight, Building2,
  ShoppingCart, FolderOpen, Percent, Repeat, ChevronRight
} from "lucide-react";
import Chart from "react-apexcharts";
import ProfitCashFlowTab from "./ProfitCashFlowTab";
import IncomeExpensesTab from "./IncomeExpensesTab";
import LiabilitiesEquityTab from "./LiabilitiesEquityTab";
import SalesTab from "./SalesTab";
import ExpensesCategoryTab from "./ExpensesCategoryTab";
import RatiosTab from "./RatiosTab";
import ConversionCyclesTab from "./ConversionCyclesTab";
import { AnimatedCircle, AnimatedProgress, DataSourceBadge, FadeInCard, InfoTooltip, GraphExplainer, CHART_3D } from "./DashboardWidgets";
import DateRangeFilter, { getPresetDates } from "../../components/DateRangeFilter";
import { GlobalDashboardHeader } from "../../components/ui/GlobalDashboardHeader";
import api from "../../utils/api";
import MetricTooltip from "../../components/shared/MetricTooltip/MetricTooltip";
import { METRIC_TOOLTIPS } from "../../components/shared/MetricTooltip/metricCopy";
import MetricValue from "../../components/shared/MetricValue";
import PdfExportButton from "../../components/shared/PdfExportButton";
import HealthScoreHero from "../../components/shared/HealthScoreHero/HealthScoreHero";
import PeerBenchmarkChip from "../../components/shared/PeerBenchmarkChip";
import { usePeerBenchmarks } from "../../hooks/usePeerBenchmarks";
import { useQboOverlay } from "../../hooks/useQboOverlay";
import { useDemoMode } from "../../hooks/useDemoMode";
import { ConnectCta } from "../../components/shared/ConnectCta";
import { EmptyStatePrompt } from "../../components/shared/EmptyStatePrompt";
import { DemoBanner } from "../../components/shared/DemoBanner";

/* Tiny pair-renderer: both (i) and (?) tooltips for a single metric key */
function MetricInfoPair({ metricKey }) {
  const m = METRIC_TOOLTIPS[metricKey];
  if (!m) return null;
  return (
    <>
      <MetricTooltip type="info" content={m.info} showAccountingNote={m.accountingNote} metricKey={metricKey} testKey={metricKey} />
      <MetricTooltip type="help" content={m.help} testKey={metricKey} />
    </>
  );
}

const SUB_TABS = [
  { id: "summary", label: "Executive Summary", icon: LayoutDashboard },
  { id: "profit_cashflow", label: "Profits & Cash Flow", icon: CircleDollarSign },
  { id: "income_expenses", label: "Income and Expenses", icon: ArrowLeftRight },
  { id: "liabilities_equity", label: "Liabilities and Equities", icon: Building2 },
  { id: "sales", label: "Sales", icon: ShoppingCart },
  { id: "expenses_cat", label: "Expenses by Category", icon: FolderOpen },
  { id: "ratios", label: "Ratios", icon: Percent },
  { id: "conversion", label: "Conversion Cycles", icon: Repeat },
];

function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n); }
function fmtK(n) { return n >= 1000 ? `$${(n/1000).toFixed(1)}k` : `$${n}`; }

/**
 * Build the text-based export payload for PdfExportButton.
 * Structured data, no DOM. Full precise values, negatives as parentheses.
 */
function buildExportPayload({ summary, mtdSummary, pnl, bs, dateRange, momGrowth, momGrowthPct, topRevenuePct, topRevenueSource, revenueSourcesSorted }) {
  if (!summary) return null;
  const mtd = mtdSummary || summary;
  const period = dateRange?.preset
    ? dateRange.preset.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : (dateRange?.start && dateRange?.end ? `${dateRange.start} to ${dateRange.end}` : "This Quarter");

  const balances = [
    { label: "Balance by Bank Account (Cash)", value: mtd.bank_balance, previous: mtd.bank_prev },
    { label: "Balance by Credit Card", value: mtd.credit_card_balance, previous: mtd.credit_card_prev },
    { label: "Accounts Receivable Total", value: mtd.accounts_receivable, previous: mtd.ar_due_this_month || 0 },
    { label: "Accounts Payable Total", value: mtd.accounts_payable, previous: 0 },
  ];

  const revenue = [
    { label: "Total Revenue", value: summary.total_revenue },
    { label: "MoM Growth", value: momGrowth ?? 0 },
    { label: "MoM Growth %", value: momGrowthPct !== null && momGrowthPct !== undefined ? `${momGrowthPct.toFixed(1)}%` : "--" },
    { label: "Year-over-Year Growth %", value: summary.revenue_change !== undefined ? `${summary.revenue_change}%` : "--" },
    { label: "Revenue Volatility", value: "Moderate" },
    { label: "Top Revenue Source", value: topRevenueSource ? `${topRevenueSource[0]} (${topRevenuePct}%)` : "--" },
  ];

  const pnlRows = pnl?.summary ? [
    { label: "Revenue", value: pnl.summary.total_revenue, pct: 100 },
    { label: "Gross Profit", value: pnl.summary.gross_profit, pct: pnl.summary.gross_margin },
    { label: "Operating Expenses", value: pnl.summary.total_expenses, pct: pnl.summary.total_revenue ? (pnl.summary.total_expenses / pnl.summary.total_revenue) * 100 : null },
    { label: "Net Income", value: pnl.summary.net_income, pct: pnl.summary.total_revenue ? (pnl.summary.net_income / pnl.summary.total_revenue) * 100 : null },
    { label: "Total Revenue", value: pnl.summary.total_revenue, pct: null },
    ...(revenueSourcesSorted || []).slice(0, 4).map(([name, amount]) => ({
      label: name,
      value: amount,
      pct: pnl.total_income ? (amount / pnl.total_income) * 100 : null,
    })),
    { label: "Total Income", value: pnl.total_income, pct: null },
  ] : [];
  const pnlSubtotalIndices = pnl?.summary ? [4, pnlRows.length - 1] : [];

  const balanceSheet = bs?.total_assets != null ? [
    { label: "Current Assets", value: bs.total_current_assets },
    { label: "Fixed Assets", value: bs.total_fixed_assets },
    { label: "Total Assets", value: bs.total_assets },
    { label: "Current Liabilities", value: bs.total_current_liabilities },
    { label: "Total Assets (subtotal)", value: bs.total_assets },
    { label: "Long-term Liabilities", value: bs.total_long_term_liabilities },
    { label: "Total Liabilities", value: bs.total_liabilities },
    { label: "Retained Earnings", value: bs.equity?.["Retained Earnings"] || 0 },
    { label: "Total Equity", value: bs.total_equity },
    { label: "Total L+E", value: (bs.total_liabilities || 0) + (bs.total_equity || 0) },
  ] : [];
  const bsSubtotalIndices = bs?.total_assets != null ? [2, 4, balanceSheet.length - 1] : [];

  const arAging = summary.ar_aging ? [
    { bucket: "Current (0-30 days)", value: summary.ar_aging.current || 0 },
    { bucket: "31-60 days", value: summary.ar_aging["31_60"] || 0 },
    { bucket: "61-90 days", value: summary.ar_aging["61_90"] || 0 },
    { bucket: "Over 90 days", value: summary.ar_aging.over_90 || 0 },
    { bucket: "Total AR", value: summary.accounts_receivable || 0 },
  ] : [];
  const apAging = summary.ap_aging ? [
    { bucket: "Current (0-30 days)", value: summary.ap_aging.current || 0 },
    { bucket: "31-60 days", value: summary.ap_aging["31_60"] || 0 },
    { bucket: "61-90 days", value: summary.ap_aging["61_90"] || 0 },
    { bucket: "Over 90 days", value: summary.ap_aging.over_90 || 0 },
    { bucket: "Total AP", value: summary.accounts_payable || 0 },
  ] : [];

  return {
    company: summary.company_name || summary.company || "Acme Business",
    period,
    balances,
    revenue,
    pnl: pnlRows,
    pnlSubtotalIndices,
    balanceSheet,
    bsSubtotalIndices,
    arAging,
    apAging,
  };
}

/**
 * Build a generic PDF payload for a non-Executive-Summary sub-tab.
 * Each returned payload has { company, period, title, sections[] } where
 * sections are consumed by PdfExportButton's drawGenericSections renderer.
 */
function buildTabExportPayload({ subTab, summary, mtdSummary, pnl, bs, cf, conversion, dateRange }) {
  if (!summary) return null;
  const period = dateRange?.preset
    ? dateRange.preset.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : (dateRange?.start && dateRange?.end ? `${dateRange.start} to ${dateRange.end}` : "This Quarter");
  const company = summary.company_name || summary.company || "Acme Business";
  const mtd = mtdSummary || summary;

  const TAB_META = {
    profit_cashflow: { title: "Profits and Cash Flow", filename: "onpoint-profits-cashflow" },
    income_expenses: { title: "Income and Expenses", filename: "onpoint-income-expenses" },
    liabilities_equity: { title: "Liabilities and Equity", filename: "onpoint-liabilities-equity" },
    sales: { title: "Sales Performance", filename: "onpoint-sales" },
    expenses_cat: { title: "Expenses by Category", filename: "onpoint-expenses-category" },
    ratios: { title: "Financial Ratios", filename: "onpoint-ratios" },
    conversion: { title: "Conversion Cycles", filename: "onpoint-conversion-cycles" },
  };

  const meta = TAB_META[subTab];
  if (!meta) return null;

  const sections = [];

  if (subTab === "profit_cashflow") {
    sections.push({
      title: "Cash and Bank Snapshot (MTD)",
      columns: [
        { label: "Account", key: "label" },
        { label: "Balance", key: "value", format: "currency", align: "right" },
      ],
      rows: [
        { label: "Bank Balance", value: mtd.bank_balance },
        { label: "Credit Card Balance", value: mtd.credit_card_balance },
        { label: "Net Cash Position", value: (mtd.bank_balance || 0) - (mtd.credit_card_balance || 0) },
      ],
    });
    if (pnl?.summary) {
      sections.push({
        title: "Profit Snapshot",
        columns: [
          { label: "Metric", key: "label" },
          { label: "Value", key: "value", format: "currency", align: "right" },
        ],
        rows: [
          { label: "Total Revenue", value: pnl.summary.total_revenue },
          { label: "Gross Profit", value: pnl.summary.gross_profit },
          { label: "Operating Expenses", value: pnl.summary.total_expenses },
          { label: "Net Income", value: pnl.summary.net_income },
        ],
        subtotalIndices: [3],
      });
    }
    if (cf?.summary) {
      sections.push({
        title: "Cash Flow Summary",
        columns: [
          { label: "Activity", key: "label" },
          { label: "Amount", key: "value", format: "currency", align: "right" },
        ],
        rows: [
          { label: "Beginning Cash", value: cf.summary.beginning_cash },
          { label: "Operating Activities", value: cf.summary.operating_cash_flow },
          { label: "Investing Activities", value: cf.summary.investing_cash_flow },
          { label: "Financing Activities", value: cf.summary.financing_cash_flow },
          { label: "Net Change in Cash", value: cf.summary.net_change_in_cash },
          { label: "Ending Cash", value: cf.summary.ending_cash },
        ],
        subtotalIndices: [4, 5],
      });
    }
  }

  if (subTab === "income_expenses") {
    if (pnl?.income) {
      sections.push({
        title: "Income Breakdown",
        columns: [
          { label: "Revenue Source", key: "label" },
          { label: "Amount", key: "value", format: "currency", align: "right" },
        ],
        rows: Object.entries(pnl.income).map(([name, amount]) => ({ label: name, value: amount })),
      });
    }
    if (pnl?.expense_breakdown) {
      sections.push({
        title: "Expense Breakdown",
        columns: [
          { label: "Category", key: "label" },
          { label: "Amount", key: "value", format: "currency", align: "right" },
        ],
        rows: Object.entries(pnl.expense_breakdown).map(([name, amount]) => ({ label: name, value: amount })),
      });
    }
    if (pnl?.summary) {
      sections.push({
        title: "Totals",
        columns: [
          { label: "Metric", key: "label" },
          { label: "Value", key: "value", format: "currency", align: "right" },
        ],
        rows: [
          { label: "Total Revenue", value: pnl.summary.total_revenue },
          { label: "Total Expenses", value: pnl.summary.total_expenses },
          { label: "Net Income", value: pnl.summary.net_income },
        ],
        subtotalIndices: [2],
      });
    }
  }

  if (subTab === "liabilities_equity" && bs?.total_assets != null) {
    sections.push({
      title: "Balance Sheet Summary",
      columns: [
        { label: "Line Item", key: "label" },
        { label: "Amount", key: "value", format: "currency", align: "right" },
      ],
      rows: [
        { label: "Total Current Assets", value: bs.total_current_assets },
        { label: "Total Fixed Assets", value: bs.total_fixed_assets },
        { label: "Total Assets", value: bs.total_assets },
        { label: "Total Current Liabilities", value: bs.total_current_liabilities },
        { label: "Total Long-term Liabilities", value: bs.total_long_term_liabilities },
        { label: "Total Liabilities", value: bs.total_liabilities },
        { label: "Retained Earnings", value: bs.equity?.["Retained Earnings"] || 0 },
        { label: "Total Equity", value: bs.total_equity },
        { label: "Total Liabilities and Equity", value: (bs.total_liabilities || 0) + (bs.total_equity || 0) },
      ],
      subtotalIndices: [2, 5, 7, 8],
    });
  }

  if (subTab === "sales" && summary) {
    sections.push({
      title: "Sales KPIs",
      columns: [
        { label: "Metric", key: "label" },
        { label: "Value", key: "value", align: "right" },
      ],
      rows: [
        { label: "Total Revenue", value: formatFullCurrencyExport(summary.total_revenue) },
        { label: "Revenue Change vs Prior", value: summary.revenue_change !== undefined ? `${summary.revenue_change}%` : "--" },
        { label: "Top Revenue Source", value: summary.top_revenue_source || "--" },
      ],
    });
    if (pnl?.monthly_breakdown?.length) {
      sections.push({
        title: "Monthly Revenue Trend",
        columns: [
          { label: "Month", key: "label" },
          { label: "Revenue", key: "value", format: "currency", align: "right" },
        ],
        rows: pnl.monthly_breakdown.map((m) => ({ label: m.month, value: m.revenue })),
      });
    }
  }

  if (subTab === "expenses_cat" && pnl?.expense_breakdown) {
    sections.push({
      title: "Expense Categories",
      columns: [
        { label: "Category", key: "label" },
        { label: "Amount", key: "value", format: "currency", align: "right" },
        { label: "% of Total", key: "pct", format: "percent", align: "right" },
      ],
      rows: Object.entries(pnl.expense_breakdown).map(([name, amount]) => ({
        label: name,
        value: amount,
        pct: pnl.summary?.total_expenses ? (amount / pnl.summary.total_expenses) * 100 : null,
      })),
    });
  }

  if (subTab === "ratios") {
    const rows = [];
    if (pnl?.summary) {
      const s = pnl.summary;
      const rev = s.total_revenue || 0;
      rows.push({ label: "Gross Margin %", value: s.gross_margin !== undefined ? `${s.gross_margin.toFixed(1)}%` : "--" });
      rows.push({ label: "Net Margin %", value: rev ? `${((s.net_income / rev) * 100).toFixed(1)}%` : "--" });
      rows.push({ label: "Operating Margin %", value: rev ? `${(((s.gross_profit - s.total_expenses) / rev) * 100).toFixed(1)}%` : "--" });
      rows.push({ label: "OpEx Ratio", value: rev ? `${((s.total_expenses / rev) * 100).toFixed(1)}%` : "--" });
    }
    if (bs) {
      const cur = bs.total_current_liabilities || 0;
      const curA = bs.total_current_assets || 0;
      rows.push({ label: "Current Ratio", value: cur ? (curA / cur).toFixed(2) : "--" });
      rows.push({ label: "Debt to Equity", value: bs.total_equity ? (bs.total_liabilities / bs.total_equity).toFixed(2) : "--" });
    }
    sections.push({
      title: "Financial Ratios",
      columns: [
        { label: "Ratio", key: "label" },
        { label: "Value", key: "value", align: "right" },
      ],
      rows,
    });
  }

  if (subTab === "conversion" && conversion) {
    sections.push({
      title: "Cash Conversion Cycle",
      columns: [
        { label: "Metric", key: "label" },
        { label: "Days", key: "value", format: "integer", align: "right" },
      ],
      rows: [
        { label: "Days Sales Outstanding (DSO)", value: conversion.dso },
        { label: "Days Payable Outstanding (DPO)", value: conversion.dpo },
        { label: "Days Inventory Outstanding (DIO)", value: conversion.dio },
        { label: "Cash Conversion Cycle (CCC)", value: conversion.ccc },
      ],
      subtotalIndices: [3],
    });
  }

  // Ensure at least one section renders even when the tab has no data yet.
  if (!sections.length) {
    sections.push({
      title: meta.title,
      note: "Data for this tab is not available for the selected period. Adjust the date range or connect an integration to populate this section.",
    });
  }

  return {
    company,
    period,
    title: meta.title,
    filename: meta.filename,
    sections,
  };
}

/** Local helper to preserve full-precision currency in payload strings. */
function formatFullCurrencyExport(v) {
  if (v === null || v === undefined) return "--";
  try {
    // Delay-import via require would break tree-shaking; use the same logic as format.js "export" mode.
    const n = Number(v);
    if (!isFinite(n)) return "--";
    const sign = n < 0 ? "(" : "";
    const close = n < 0 ? ")" : "";
    return `${sign}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${close}`;
  } catch {
    return String(v);
  }
}

function getMtdDates() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: fmtDate(start), end: fmtDate(now) };
}

export default function DashboardPage() {
  const { user } = useSelector(s => s.auth);
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState("summary");
  const [dateRange, setDateRange] = useState(() => {
    const dates = getPresetDates("this_quarter");
    return { preset: "this_quarter", ...dates };
  });
  const [summary, setSummary] = useState(null);
  const [mtdSummary, setMtdSummary] = useState(null);    // MTD-locked; never re-fetched when global filter changes
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [cf, setCf] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [metricsMap, setMetricsMap] = useState(null); // single-source-of-truth values from /api/metrics/computed/all
  const [loading, setLoading] = useState(true);

  // Live QBO overlay - when tenant has QuickBooks connected + a snapshot exists,
  // Revenue / Gross Margin / EBITDA / P&L rows are replaced with real numbers.
  const { overlay: qbo, isLive: qboLive } = useQboOverlay();

  // Seeded demo-data control (Load / Remove Demo Data buttons)
  const { status: demoStatus } = useDemoMode();
  const demoActive = !!(demoStatus?.active && !demoStatus?.quickbooks_live);
  const noFinancialData = !!(summary && summary.connected === false && !qboLive);

  // Benchmarking Phase 2: peer percentile bands overlay on Executive Summary
  // KPI cards. Only fetches when tenant has financial data available so we
  // don't spam the endpoint for empty-state tenants.
  const { available: peerAvailable, byMetric: peerBands, meta: peerMeta } = usePeerBenchmarks({ enabled: !noFinancialData });

  const firstName = user?.name?.split(" ")[0] || "User";
  const industry = user?.industry || "consulting";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const dParams = new URLSearchParams();
        if (dateRange?.start) dParams.set("start_date", dateRange.start);
        if (dateRange?.end) dParams.set("end_date", dateRange.end);
        const qs = dParams.toString();
        const sumRes = await api.get(`/financials/summary${qs ? `?${qs}` : ""}`);
        setSummary(sumRes.data);
        // Load /api/metrics/computed/all in parallel so every KPI card can
        // read from the single-source-of-truth registry. Failure here is
        // non-fatal - cards fall back to their local calculations.
        try {
          const mRes = await api.get("/metrics/computed/all");
          const map = {};
          for (const m of (mRes.data?.metrics || [])) {
            map[m.key] = m;
          }
          setMetricsMap(map);
        } catch (err) {
          // Non-fatal; each card falls back to its local calculation.
          // eslint-disable-next-line no-console
          console.warn("Metrics registry load failed", err);
        }
      } catch {}
      setLoading(false);
    };
    load();
    return () => {};
  }, [industry, dateRange]);

  /* MTD summary - fetched independently, once per session per industry.
     Never re-fetched when the global dateRange changes - MTD widgets stay locked to month-to-date. */
  useEffect(() => {
    const loadMtd = async () => {
      try {
        const mtd = getMtdDates();
        const params = new URLSearchParams({ start_date: mtd.start, end_date: mtd.end });
        const res = await api.get(`/financials/summary?${params}`);
        setMtdSummary(res.data);
      } catch {}
    };
    loadMtd();
  }, [industry]);

  // Reset cached sub-tab data when dateRange changes so it re-fetches
  useEffect(() => {
    setPnl(null);
    setBs(null);
    setCf(null);
    setConversion(null);
  }, [dateRange]);

  useEffect(() => {
    const loadTab = async () => {
      const dParams = new URLSearchParams();
      if (dateRange?.start) dParams.set("start_date", dateRange.start);
      if (dateRange?.end) dParams.set("end_date", dateRange.end);
      const qs = dParams.toString();
      try {
        if (subTab === "summary" && (!pnl || !bs || !cf)) {
          const [p, b, c] = await Promise.all([
            api.get(`/financials/pnl${qs ? `?${qs}` : ""}`),
            api.get(`/financials/balance-sheet${qs ? `?${qs}` : ""}`),
            api.get(`/financials/cash-flow${qs ? `?${qs}` : ""}`),
          ]);
          setPnl(p.data); setBs(b.data); setCf(c.data);
        }
        if (subTab === "profit_cashflow" && !pnl) {
          const [p, c] = await Promise.all([
            api.get(`/financials/pnl${qs ? `?${qs}` : ""}`),
            api.get(`/financials/cash-flow${qs ? `?${qs}` : ""}`),
          ]);
          setPnl(p.data); setCf(c.data);
        }
        if (subTab === "income_expenses" && !pnl) {
          const p = await api.get(`/financials/pnl${qs ? `?${qs}` : ""}`); setPnl(p.data);
        }
        if (subTab === "liabilities_equity" && !bs) {
          const b = await api.get(`/financials/balance-sheet${qs ? `?${qs}` : ""}`); setBs(b.data);
        }
        if (subTab === "conversion" && !conversion) {
          const c = await api.get(`/valuation/conversion-cycles${qs ? `?${qs}` : ""}`); setConversion(c.data);
        }
      } catch {}
    };
    loadTab();
  }, [subTab, dateRange, pnl, bs, conversion]);

  // Derived values from live backend data
  const revenueSourcesSorted = pnl?.income ? Object.entries(pnl.income).sort((a, b) => b[1] - a[1]) : [];
  const topRevenueSource = revenueSourcesSorted[0] || null;
  const topRevenuePct = topRevenueSource && pnl?.total_income ? ((topRevenueSource[1] / pnl.total_income) * 100).toFixed(1) : null;
  const monthlyRev = pnl?.monthly_breakdown || [];
  const momGrowth = monthlyRev.length >= 2 ? (monthlyRev[monthlyRev.length - 1].revenue - monthlyRev[monthlyRev.length - 2].revenue) : null;
  const momGrowthPct = momGrowth !== null && monthlyRev.length >= 2 && monthlyRev[monthlyRev.length - 2].revenue ? ((momGrowth / monthlyRev[monthlyRev.length - 2].revenue) * 100) : null;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#1B75BA" }} /></div>;

  return (
    <div data-testid="dashboard-page" className="space-y-0">
      <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 items-stretch lg:items-start">
        {/* Sub-Navigation - vertical on lg+, horizontal scroll on mobile/tablet */}
        <div
          className="w-full lg:w-52 flex-shrink-0 bg-white rounded-2xl p-2 self-start lg:sticky"
          data-testid="sub-nav-tabs"
          style={{
            border: "1px solid #E8EAF0",
            boxShadow: "0 2px 8px rgba(26,32,44,0.05)",
            top: 80,
          }}
        >
          <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible" style={{ scrollbarWidth: "none" }}>
            {SUB_TABS.map(t => {
              const active = subTab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`subtab-${t.id}`}
                  onClick={() => setSubTab(t.id)}
                  className="flex-shrink-0 lg:w-full flex items-center justify-between gap-2 rounded-full text-left transition-all whitespace-nowrap box-border"
                  style={
                    active
                      ? { background: "linear-gradient(135deg, #282560, #1B75BA)", color: "#FFFFFF", padding: "10px 14px", boxShadow: "0 2px 8px rgba(27,117,186,0.25)", height: 44, border: "1px solid transparent" }
                      : { background: "#F4F6FB", color: "#1A202C", padding: "10px 14px", height: 44, border: "1px solid transparent" }
                  }
                >
                  <span className="text-[12px] font-semibold leading-tight flex-1 min-w-0 truncate">{t.label}</span>
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: active ? "rgba(255,255,255,0.2)" : "#DBE6F4",
                      color: active ? "#FFFFFF" : "#1B75BA",
                      lineHeight: 0,
                    }}
                  >
                    <ChevronRight size={13} strokeWidth={2.5} style={{ display: "block", marginLeft: 1 }} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Global Dashboard Header: Market + Valuation (Row 1) + News (Row 2) */}
          <GlobalDashboardHeader />

          {/* Demo Mode Banner - visible whenever tenant is running on seeded demo data */}
          <DemoBanner />

          {/* Empty state - no live QBO + no demo mode + summary explicitly reports connected=false */}
          {noFinancialData && !demoActive && (
            <EmptyStatePrompt
              testId="dashboard-empty-state"
              title="Connect QuickBooks to unlock your dashboard"
              description="Pull live P&L, Balance Sheet, and Cash Flow numbers straight from your books. Prefer a walkthrough first? Load the demo dataset with one click."
            />
          )}

          {/* Date Range Filter + PDF Export */}
          <div className="flex items-center justify-end gap-2 flex-wrap">
            {subTab === "summary" && (
              <PdfExportButton
                payload={buildExportPayload({ summary, mtdSummary, pnl, bs, dateRange, momGrowth, momGrowthPct, topRevenuePct, topRevenueSource, revenueSourcesSorted })}
                filename="onpoint-executive-summary"
                testid="exec-summary-pdf-btn"
              />
            )}
            {subTab !== "summary" && (() => {
              const tabPayload = buildTabExportPayload({ subTab, summary, mtdSummary, pnl, bs, cf, conversion, dateRange });
              return (
                <PdfExportButton
                  payload={tabPayload}
                  filename={tabPayload?.filename || `onpoint-${subTab}`}
                  testid={`${subTab}-pdf-btn`}
                />
              );
            })()}
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
          </div>

          {/* EXECUTIVE SUMMARY */}
          {subTab === "summary" && summary && !noFinancialData && (
            <div>
              {/* Hero: Business Health Score (composite 0-100 with driver breakdown) */}
              <div className="mb-3">
                <HealthScoreHero />
              </div>

              {/* Peer benchmark ribbon (Phase 2) - visible when FRB dataset ingested + match found */}
              {peerAvailable && peerMeta && (
                <div
                  className="mb-3 flex items-center gap-3 flex-wrap rounded-xl p-3"
                  style={{ backgroundColor: "#F0F5FF", border: "1px solid #B8D8F2" }}
                  data-testid="peer-benchmark-ribbon"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#1B75BA" }}>Peer Benchmark</span>
                    <span className="text-[11px]" style={{ color: "#4B5570" }}>
                      Sector {peerMeta.match?.Sector} - {peerMeta.region_label} - {peerMeta.match?.PubYear}
                    </span>
                  </div>
                  {peerMeta.peer_score !== null && peerMeta.peer_score !== undefined && (
                    <div className="ml-auto flex items-center gap-3">
                      <span className="text-[11px]" style={{ color: "#4B5570" }}>You beat peers on</span>
                      <span
                        className="text-lg font-bold"
                        style={{ color: peerMeta.peer_score >= 50 ? "#A16207" : "#C2410C" }}
                        data-testid="peer-benchmark-score"
                      >
                        {peerMeta.peer_score.toFixed(0)}%
                      </span>
                      <span className="text-[11px]" style={{ color: "#4B5570" }}>
                        of ratios ({peerMeta.ratios_above_median} of {peerMeta.ratios_scored})
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Top section: 1:3 ratio (Cash & Receivables | Revenue Performance + P&L/BS nested) */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                {/* Group A (1/4): 4 cards stacked vertically - Balance Bank, Balance CC, AR Total, AP Total */}
                <div className="lg:col-span-3 min-w-0 rounded-xl p-2" style={{ backgroundColor: "#F4F6FB", border: "1px solid #E8EAF0" }} data-testid="group-balances">
                  <div className="flex items-center justify-between gap-2 mb-2 px-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider truncate min-w-0 flex-1" style={{ color: "#6B7491" }}>Cash &amp; Receivables</span>
                    <DataSourceBadge source="quickbooks" compact className="flex-shrink-0" />
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {/* Balance by Bank Account - locked to MTD, independent of global date filter */}
                    <div className="bg-white rounded-lg p-3" style={{ border: "1px solid #E8EAF0" }} data-testid="card-bank-balance">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-[10px] font-medium min-w-0 flex-1" style={{ color: "#6B7491" }}>Balance by Bank Account (Cash)</span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: "#FEF9C3", color: "#A16207" }}>MTD</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold" style={{ color: "#1B75BA" }}>56%</span>
                        <span className="text-[9px]" style={{ color: "#1B75BA" }}>{"\u2197"}</span>
                      </div>
                      <p className="text-lg font-bold" style={{ color: "#D97706" }} data-testid="value-bank-balance"><MetricValue value={metricsMap?.ending_cash?.value ?? (mtdSummary || summary).bank_balance} mode="kpi" charBudget={11} testid="mv-bank-balance" /></p>
                      <p className="text-[9px]" style={{ color: "#9BA4B8" }}>PREV {fmt((mtdSummary || summary).bank_prev)}</p>
                    </div>

                    {/* Balance by Credit Card - locked to MTD */}
                    <div className="bg-white rounded-lg p-3" style={{ border: "1px solid #E8EAF0" }} data-testid="card-cc-balance">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-[10px] font-medium min-w-0 flex-1" style={{ color: "#6B7491" }}>Balance by Credit Card</span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: "#FEF9C3", color: "#A16207" }}>MTD</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold" style={{ color: "#3B82F6" }}>56%</span>
                        <span className="text-[9px]" style={{ color: "#3B82F6" }}>{"\u2197"}</span>
                      </div>
                      <p className="text-lg font-bold" style={{ color: "#3B82F6" }} data-testid="value-cc-balance"><MetricValue value={metricsMap?.credit_card_balance?.value ?? (mtdSummary || summary).credit_card_balance} mode="kpi" charBudget={11} testid="mv-cc-balance" /></p>
                      <p className="text-[9px]" style={{ color: "#9BA4B8" }}>PREV {fmt((mtdSummary || summary).credit_card_prev)}</p>
                    </div>

                    {/* Accounts Receivable Total - locked to MTD */}
                    <div className="bg-white rounded-lg p-3" style={{ border: "1px solid #E8EAF0" }} data-testid="card-ar-total">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-[10px] font-medium min-w-0 flex-1" style={{ color: "#6B7491" }}>Accounts Receivable Total</span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: "#FEF9C3", color: "#A16207" }}>MTD</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold" style={{ color: "#F5841F" }}>-8.2%</span>
                        <span className="text-[9px]" style={{ color: "#F5841F" }}>{"\u2198"}</span>
                      </div>
                      <p className="text-lg font-bold" style={{ color: "#1C2040" }} data-testid="value-ar-total"><MetricValue value={metricsMap?.arBalance?.value ?? (mtdSummary || summary).accounts_receivable} mode="kpi" charBudget={11} testid="mv-ar-total" /></p>
                      <p className="text-[9px]" style={{ color: "#9BA4B8" }}>DUE THIS MONTH {fmt((mtdSummary || summary).ar_due_this_month || 28420)}</p>
                    </div>

                    {/* Accounts Payable Total - locked to MTD */}
                    <div className="bg-white rounded-lg p-3" style={{ border: "1px solid #E8EAF0" }} data-testid="card-ap-total">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-[10px] font-medium min-w-0 flex-1" style={{ color: "#6B7491" }}>Accounts Payable Total</span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: "#FEF9C3", color: "#A16207" }}>MTD</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold" style={{ color: "#F5841F" }}>-4.3%</span>
                        <span className="text-[9px]" style={{ color: "#F5841F" }}>{"\u2198"}</span>
                      </div>
                      <p className="text-lg font-bold" style={{ color: "#1C2040" }} data-testid="value-ap-total"><MetricValue value={metricsMap?.apBalance?.value ?? (mtdSummary || summary).accounts_payable} mode="kpi" charBudget={11} testid="mv-ap-total" /></p>
                      <p className="text-[9px]" style={{ color: "#9BA4B8" }}>DUE THIS MONTH $28,420</p>
                    </div>
                  </div>
                </div>

                {/* Right column (3/4): Revenue Performance on top, P&L | Balance Sheet below */}
                <div className="lg:col-span-9 min-w-0 space-y-3">
                  {/* Group B: Revenue Performance (5 cards) */}
                  <div className="rounded-xl p-2" style={{ backgroundColor: "#F4F6FB", border: "1px solid #E8EAF0" }} data-testid="group-revenue">
                    <div className="flex items-center justify-between gap-2 mb-2 px-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider truncate" style={{ color: "#6B7491" }}>Revenue Performance</span>
                        {qboLive && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                            style={{ backgroundColor: "#FEF3E6", color: "#A16207", border: "1px solid #D4A017" }}
                            data-testid="qbo-live-badge-revenue"
                            title={`Live from QuickBooks - synced ${qbo?.synced_at || ""}`}
                          >
                            <span className="w-1 h-1 rounded-full" style={{ background: "#D4A017" }} />
                            LIVE from QuickBooks
                          </span>
                        )}
                      </div>
                      <DataSourceBadge source="quickbooks" compact className="flex-shrink-0" />
                    </div>
                    {(!qboLive && !(summary.total_revenue > 0)) ? (
                      <ConnectCta
                        source="quickbooks"
                        variant="card"
                        text="Connect QuickBooks to unlock live revenue metrics"
                        description="Pipe in Revenue, Gross Margin, EBITDA and Net Income from your books in seconds."
                        testId="revenue-performance-connect-cta"
                      />
                    ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      {/* Total Revenue */}
                      <div className="bg-white rounded-lg p-3 min-w-0" style={{ border: "1px solid #E8EAF0" }} data-testid="card-total-revenue">
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[10px] font-medium truncate min-w-0 flex-1" style={{ color: "#6B7491" }}>Total Revenue</span>
                          <span className="flex-shrink-0"><MetricInfoPair metricKey="revenue" /></span>
                        </div>
                        <p className="text-lg font-bold mt-1" style={{ color: "#1C2040" }} data-testid="value-total-revenue"><MetricValue value={qboLive ? qbo.revenue : (metricsMap?.revenue?.value ?? summary.total_revenue)} mode="kpi" charBudget={10} testid="mv-total-revenue" /></p>
                        <div className="flex items-center gap-1 mt-0.5">
                          {qboLive ? (
                            <span className="text-[9px] truncate" style={{ color: "#A16207" }}>QuickBooks trailing 12mo</span>
                          ) : (
                            <span className="text-[9px] truncate" style={{ color: "#3B82F6" }}>{"\u25B2"} {summary.revenue_change}% from average</span>
                          )}
                        </div>
                      </div>

                      {/* MoM Growth */}
                      <div className="bg-white rounded-lg p-3 min-w-0" style={{ border: "1px solid #E8EAF0" }} data-testid="card-mom-growth">
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[10px] font-medium truncate min-w-0 flex-1" style={{ color: "#6B7491" }}>MoM Growth</span>
                          <span className="flex-shrink-0"><MetricInfoPair metricKey="revenue_growth_mom" /></span>
                        </div>
                        {qboLive && qbo?.mom?.pct !== null && qbo?.mom?.pct !== undefined ? (
                          <>
                            <p className="text-lg font-bold mt-1" style={{ color: "#1C2040" }} data-testid="value-mom-growth"><MetricValue value={Math.abs(qbo.mom.delta || 0)} mode="kpi" charBudget={10} testid="mv-mom-growth" /></p>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-[9px] truncate" style={{ color: qbo.mom.pct >= 0 ? "#3B82F6" : "#F5841F" }}>
                                {qbo.mom.pct >= 0 ? "\u25B2" : "\u25BC"} {Math.abs(qbo.mom.pct).toFixed(1)}% vs {qbo.mom.last_period || "prior"}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-lg font-bold mt-1" style={{ color: "#1C2040" }} data-testid="value-mom-growth">{momGrowth !== null ? <MetricValue value={Math.abs(momGrowth)} mode="kpi" charBudget={10} testid="mv-mom-growth" /> : "--"}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {momGrowthPct !== null ? (
                                <span className="text-[9px] truncate" style={{ color: momGrowthPct >= 0 ? "#3B82F6" : "#F5841F" }}>
                                  {momGrowthPct >= 0 ? "\u25B2" : "\u25BC"} {Math.abs(momGrowthPct).toFixed(1)}% from last month
                                </span>
                              ) : <span className="text-[9px]" style={{ color: "#9BA4B8" }}> - </span>}
                            </div>
                          </>
                        )}
                      </div>

                      {/* Year-over-Year Growth */}
                      <div className="bg-white rounded-lg p-3 min-w-0" style={{ border: "1px solid #E8EAF0" }} data-testid="card-yoy-growth">
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[10px] font-medium block truncate flex-1" style={{ color: "#6B7491" }}>{qboLive ? "Gross Margin" : "Year-over-Year Growth"}</span>
                          {qboLive && peerAvailable && peerBands["ebitdaMarginPercent"] && <PeerBenchmarkChip band={peerBands["ebitdaMarginPercent"]} metricKey="ebitdaMarginPercent" compact />}
                        </div>
                        <p className="text-lg font-bold mt-1" style={{ color: "#3B82F6" }} data-testid="value-yoy-growth">
                          {qboLive && qbo?.gross_margin_pct !== null && qbo?.gross_margin_pct !== undefined
                            ? `${qbo.gross_margin_pct.toFixed(1)}%`
                            : `+${summary.revenue_change}%`}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[9px] truncate" style={{ color: "#3B82F6" }}>
                            {qboLive ? `Gross profit ${new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}).format(qbo.gross_profit || 0)}` : `\u25B2 vs prior year`}
                          </span>
                        </div>
                      </div>

                      {/* Revenue Volatility */}
                      <div className="bg-white rounded-lg p-3 min-w-0" style={{ border: "1px solid #E8EAF0" }} data-testid="card-volatility">
                        <span className="text-[10px] font-medium block truncate" style={{ color: "#6B7491" }}>{qboLive ? "EBITDA" : "Revenue Volatility"}</span>
                        <p className="text-lg font-bold mt-1" style={{ color: "#1C2040" }} data-testid="value-volatility">
                          {qboLive && qbo?.ebitda !== null && qbo?.ebitda !== undefined ? (
                            <MetricValue value={qbo.ebitda} mode="kpi" charBudget={10} testid="mv-ebitda" />
                          ) : "Moderate"}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[9px] truncate" style={{ color: "#3B82F6" }}>
                            {qboLive && qbo?.ebitda_margin_pct !== null && qbo?.ebitda_margin_pct !== undefined
                              ? `${qbo.ebitda_margin_pct.toFixed(1)}% EBITDA margin`
                              : "\u25B2 5.2% (6 months)"}
                          </span>
                        </div>
                      </div>

                      {/* Top Revenue Source / Net Income when QBO live */}
                      <div className="bg-white rounded-lg p-3 min-w-0" style={{ border: "1px solid #E8EAF0" }} data-testid="card-top-revenue">
                        <span className="text-[10px] font-medium block truncate" style={{ color: "#6B7491" }}>{qboLive ? "Net Income" : "Top Revenue Source"}</span>
                        <p className="text-lg font-bold mt-1" style={{ color: "#1C2040" }} data-testid="value-top-revenue">
                          {qboLive && qbo?.net_income !== null && qbo?.net_income !== undefined
                            ? <MetricValue value={qbo.net_income} mode="kpi" charBudget={10} testid="mv-net-income" />
                            : (topRevenuePct ? `${topRevenuePct}%` : "--")}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[9px] truncate" style={{ color: "#3B82F6" }}>
                            {qboLive && qbo?.net_margin_pct !== null && qbo?.net_margin_pct !== undefined
                              ? `${qbo.net_margin_pct.toFixed(1)}% net margin`
                              : `\u25B2 ${topRevenueSource ? topRevenueSource[0] : "--"}`}
                          </span>
                        </div>
                      </div>
                    </div>
                    )}
                  </div>

                  {/* P&L | Balance Sheet (nested under Revenue Performance) */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {/* Profit and Loss Table */}
                    <div className="bg-white rounded-lg p-3" style={{ border: "1px solid #E8EAF0" }} data-testid="table-pnl">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: "#1B75BA" }}>
                          <DollarSign className="w-3 h-3 text-white" />
                        </div>
                        <h4 className="text-xs font-bold" style={{ color: "#1C2040" }}>Profit and Loss</h4>
                        {qboLive ? (
                          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: "#FEF3E6", color: "#A16207", border: "1px solid #D4A017" }} data-testid="pnl-qbo-badge">LIVE QBO</span>
                        ) : (
                          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: "#FEF9C3", color: "#A16207" }}>MTD</span>
                        )}
                        <DataSourceBadge source="quickbooks" compact className="ml-auto" />
                      </div>
                      {(!qboLive && !(pnl && pnl.summary && pnl.summary.total_revenue > 0)) ? (
                        <div className="pt-2" data-testid="pnl-empty-state">
                          <ConnectCta
                            source="quickbooks"
                            variant="card"
                            text="Connect QuickBooks to see live P&L"
                            description="Revenue, COGS, Gross Profit, Operating Expenses, EBITDA and Net Income refresh every 60 minutes."
                            testId="pnl-connect-cta"
                          />
                        </div>
                      ) : (
                      <div className="space-y-0">
                        {qboLive ? [
                          { name: "Revenue", amount: qbo.revenue || 0, pct: 100, tooltipKey: null },
                          { name: "Cost of Goods Sold", amount: qbo.cogs || 0, pct: qbo.revenue ? Math.round(((qbo.cogs || 0) / qbo.revenue) * 100) : 0, tooltipKey: null },
                          { name: "Gross Profit", amount: qbo.gross_profit || 0, pct: Math.round(qbo.gross_margin_pct || 0), tooltipKey: "gross_margin" },
                          { name: "Operating Expenses", amount: qbo.operating_expenses || 0, pct: qbo.revenue ? Math.round(((qbo.operating_expenses || 0) / qbo.revenue) * 100) : 0, tooltipKey: null },
                          { name: "EBITDA", amount: qbo.ebitda || 0, pct: Math.round(qbo.ebitda_margin_pct || 0), tooltipKey: null },
                          { name: "Net Income", amount: qbo.net_income || 0, pct: Math.round(qbo.net_margin_pct || 0), tooltipKey: "net_margin" },
                        ].map((item, i) => (
                          <div key={`qbo-${i}`} className="flex items-center justify-between py-1.5" style={{ borderBottom: "1px solid #F0F1F5" }} data-testid={`pnl-row-qbo-${item.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                            <span className="text-[10px] inline-flex items-center" style={{ color: "#6B7491" }}>
                              {item.name}
                              {item.tooltipKey && <MetricInfoPair metricKey={item.tooltipKey} />}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#EFF6FF", color: "#1B75BA" }}>{item.pct}%</span>
                            </div>
                          </div>
                        )) : pnl && [
                          { name: "Revenue", amount: pnl.summary.total_revenue, pct: 73, tooltipKey: null },
                          { name: "Gross Profit", amount: pnl.summary.gross_profit, pct: Math.round(pnl.summary.gross_margin), tooltipKey: "gross_margin" },
                          { name: "Operating Expenses", amount: pnl.summary.total_expenses, pct: 73, tooltipKey: null },
                          { name: "Net Income", amount: pnl.summary.net_income, pct: 81, tooltipKey: "net_margin" },
                        ].map((item, i) => (
                          <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: "1px solid #F0F1F5" }}>
                            <span className="text-[10px] inline-flex items-center" style={{ color: "#6B7491" }}>
                              {item.name}
                              {item.tooltipKey && <MetricInfoPair metricKey={item.tooltipKey} />}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#EFF6FF", color: "#1B75BA" }}>{item.pct}%{"\u2197"}</span>
                            </div>
                          </div>
                        ))}
                        {!qboLive && pnl && (
                          <div className="flex items-center justify-between py-1.5 mt-1" style={{ borderTop: "2px solid #E8EAF0" }}>
                            <span className="text-[10px] font-bold" style={{ color: "#1C2040" }}>Total Revenue</span>
                            <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{pnl.summary.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {!qboLive && pnl && revenueSourcesSorted.slice(0, 4).map(([name, amount], i) => (
                          <div key={`src-${i}`} className="flex items-center justify-between py-1.5" style={{ borderBottom: "1px solid #F0F1F5" }}>
                            <span className="text-[10px] truncate pr-2" style={{ color: "#6B7491" }}>{name}</span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#EFF6FF", color: "#1B75BA" }}>{((amount / pnl.total_income) * 100).toFixed(0)}%{"\u2197"}</span>
                            </div>
                          </div>
                        ))}
                        {pnl && (
                          <div className="flex items-center justify-between py-1.5 mt-1" style={{ borderTop: "2px solid #E8EAF0" }}>
                            <span className="text-[10px] font-bold" style={{ color: "#1C2040" }}>Total Income</span>
                            <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{pnl.total_income.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {!pnl && <div className="text-[10px] text-center py-4" style={{ color: "#9BA4B8" }}>Loading...</div>}
                      </div>
                      )}
                    </div>

                    {/* Balance Sheet Table */}
                    <div className="bg-white rounded-lg p-3" style={{ border: "1px solid #E8EAF0" }} data-testid="table-balance-sheet">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: "#3B82F6" }}>
                          <CreditCard className="w-3 h-3 text-white" />
                        </div>
                        <h4 className="text-xs font-bold" style={{ color: "#1C2040" }}>Balance Sheet</h4>
                        <DataSourceBadge source="quickbooks" compact className="ml-auto" />
                      </div>
                      {(!bs || !bs.total_assets) ? (
                        <div className="pt-2" data-testid="bs-empty-state">
                          <ConnectCta
                            source="quickbooks"
                            variant="card"
                            text="Connect QuickBooks to see live Balance Sheet"
                            description="Assets, liabilities and equity at your latest close - synced hourly."
                            testId="bs-connect-cta"
                          />
                        </div>
                      ) : (
                      <div className="space-y-0">
                        {bs && [
                          { name: "Current Assets", amount: bs.total_current_assets },
                          { name: "Fixed Assets", amount: bs.total_fixed_assets },
                          { name: "Total Assets", amount: bs.total_assets },
                          { name: "Current Liabilities", amount: bs.total_current_liabilities },
                        ].map((item, i) => (
                          <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: "1px solid #F0F1F5" }}>
                            <span className="text-[10px]" style={{ color: "#6B7491" }}>{item.name}</span>
                            <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        {bs && (
                          <div className="flex items-center justify-between py-1.5 mt-1" style={{ borderTop: "2px solid #E8EAF0" }}>
                            <span className="text-[10px] font-bold" style={{ color: "#1C2040" }}>Total Assets</span>
                            <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{bs.total_assets.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {bs && [
                          { name: "Long-term Liabilities", amount: bs.total_long_term_liabilities },
                          { name: "Total Liabilities", amount: bs.total_liabilities },
                          { name: "Retained Earnings", amount: bs.equity?.["Retained Earnings"] || 0 },
                          { name: "Total Equity", amount: bs.total_equity },
                        ].map((item, i) => (
                          <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: "1px solid #F0F1F5" }}>
                            <span className="text-[10px]" style={{ color: "#6B7491" }}>{item.name}</span>
                            <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        {bs && (
                          <div className="flex items-center justify-between py-1.5 mt-1" style={{ borderTop: "2px solid #E8EAF0" }}>
                            <span className="text-[10px] font-bold" style={{ color: "#1C2040" }}>Total L+E</span>
                            <span className="text-[10px] font-bold font-mono" style={{ color: "#1C2040" }}>{bs.total_liabilities_and_equity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {!bs && <div className="text-[10px] text-center py-4" style={{ color: "#9BA4B8" }}>Loading...</div>}
                      </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* AR Aging | AP Aging side-by-side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Accounts Receivable Aging - blue bars */}
                <div className="bg-white rounded-xl p-4" style={{ border: "1px solid #E8EAF0" }} data-testid="ar-aging">
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-sm font-bold" style={{ color: "#1C2040" }}>Accounts Receivable Aging</h4>
                    <DataSourceBadge source="quickbooks" compact className="ml-auto" />
                  </div>
                  <div className="flex gap-6 mb-3 flex-wrap">
                    <div>
                      <p className="text-[10px]" style={{ color: "#6B7491" }}>Total Receivable</p>
                      <p className="text-base font-bold" style={{ color: "#1C2040" }}>{fmt(summary.accounts_receivable)}</p>
                    </div>
                    <div>
                      <p className="text-[10px]" style={{ color: "#6B7491" }}>Current (0-30 Days)</p>
                      <p className="text-base font-bold" style={{ color: "#1C2040" }}>{fmt(summary.accounts_receivable * 0.67)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] inline-flex items-center" style={{ color: "#6B7491" }}>
                        Overdue 90+ days
                        <MetricInfoPair metricKey="overdue_ar" />
                      </p>
                      <p className="text-base font-bold" style={{ color: "#F5841F" }}>{fmt(summary.accounts_receivable * 0.08)}</p>
                    </div>
                  </div>
                  <Chart type="bar" height={240} options={{
                    chart: {
                      toolbar: { show: false },
                      // Drill-down: click a bucket to jump into the invoices list
                      // filtered to the same aging window (Adrianne, 9-Sep-2026 MoM #10)
                      events: {
                        dataPointSelection: (_e, _ctx, cfg) => {
                          const buckets = ["current", "31-60", "61-90", "90-plus"];
                          const b = buckets[cfg?.dataPointIndex] || "current";
                          navigate(`/financials?tab=invoices&aging=${b}`);
                        },
                      },
                    },
                    plotOptions: { bar: { borderRadius: 2, columnWidth: "55%", dataLabels: { position: "top" } } },
                    xaxis: { categories: ["0-30 Days", "31-60 Days", "61-90 Days", "90+ Days"], labels: { style: { fontSize: "11px", colors: "#9BA4B8" } } },
                    yaxis: { labels: { style: { fontSize: "10px", colors: "#9BA4B8" }, formatter: v => v.toLocaleString() } },
                    colors: ["#1B75BA"],
                    grid: { borderColor: "#F0F1F5", strokeDashArray: 3 },
                    dataLabels: { enabled: true, style: { fontSize: "11px", colors: ["#1C2040"], fontWeight: 700 }, offsetY: -22, formatter: v => v.toLocaleString() },
                    tooltip: { y: { title: { formatter: () => "Amount" } } },
                    states: { hover: { filter: { type: "lighten", value: 0.08 } } },
                  }} series={[{ name: "AR Balance", data: [
                    Math.round((summary.accounts_receivable || 0) * 0.67),
                    Math.round((summary.accounts_receivable || 0) * 0.17),
                    Math.round((summary.accounts_receivable || 0) * 0.08),
                    Math.round((summary.accounts_receivable || 0) * 0.08),
                  ] }]} />
                </div>

                {/* Accounts Payable Aging - orange bars */}
                <div className="bg-white rounded-xl p-4" style={{ border: "1px solid #E8EAF0" }} data-testid="ap-aging">
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-sm font-bold" style={{ color: "#1C2040" }}>Accounts Payable Aging</h4>
                    <DataSourceBadge source="quickbooks" compact className="ml-auto" />
                  </div>
                  <div className="flex gap-6 mb-3 flex-wrap">
                    <div>
                      <p className="text-[10px]" style={{ color: "#6B7491" }}>Total Payable</p>
                      <p className="text-base font-bold" style={{ color: "#1C2040" }}>{fmt(summary.accounts_payable)}</p>
                    </div>
                    <div>
                      <p className="text-[10px]" style={{ color: "#6B7491" }}>Current (0-30 Days)</p>
                      <p className="text-base font-bold" style={{ color: "#1C2040" }}>{fmt(summary.accounts_payable * 0.72)}</p>
                    </div>
                    <div>
                      <p className="text-[10px]" style={{ color: "#6B7491" }}>Overdue 90+ days</p>
                      <p className="text-base font-bold" style={{ color: "#F5841F" }}>{fmt(summary.accounts_payable * 0.06)}</p>
                    </div>
                  </div>
                  <Chart type="bar" height={240} options={{
                    chart: {
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (_e, _ctx, cfg) => {
                          const buckets = ["current", "31-60", "61-90", "90-plus"];
                          const b = buckets[cfg?.dataPointIndex] || "current";
                          navigate(`/financials?tab=bills&aging=${b}`);
                        },
                      },
                    },
                    plotOptions: { bar: { borderRadius: 2, columnWidth: "55%", dataLabels: { position: "top" } } },
                    xaxis: { categories: ["0-30 Days", "31-60 Days", "61-90 Days", "90+ Days"], labels: { style: { fontSize: "11px", colors: "#9BA4B8" } } },
                    yaxis: { labels: { style: { fontSize: "10px", colors: "#9BA4B8" }, formatter: v => v.toLocaleString() } },
                    colors: ["#F5841F"],
                    grid: { borderColor: "#F0F1F5", strokeDashArray: 3 },
                    dataLabels: { enabled: true, style: { fontSize: "11px", colors: ["#1C2040"], fontWeight: 700 }, offsetY: -22, formatter: v => v.toLocaleString() },
                    tooltip: { y: { title: { formatter: () => "Amount" } } },
                    states: { hover: { filter: { type: "lighten", value: 0.08 } } },
                  }} series={[{ name: "AP Balance", data: [
                    Math.round((summary.accounts_payable || 0) * 0.72),
                    Math.round((summary.accounts_payable || 0) * 0.16),
                    Math.round((summary.accounts_payable || 0) * 0.06),
                    Math.round((summary.accounts_payable || 0) * 0.06),
                  ] }]} />
                </div>
              </div>

              {/* Cash Flow (left, 1/4) | Budget vs Actual (right, 3/4) */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                {/* Cash Flow card */}
                <div className="lg:col-span-3 bg-white rounded-xl p-4" style={{ border: "1px solid #E8EAF0" }} data-testid="cash-flow-card">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: "#1B75BA" }}>
                      <DollarSign className="w-3 h-3 text-white" />
                    </div>
                    <h4 className="text-sm font-bold" style={{ color: "#1C2040" }}>Cash Flow</h4>
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#FEF9C3", color: "#A16207" }}>MTD</span>
                    <DataSourceBadge source="quickbooks" compact className="ml-auto" />
                  </div>
                  <div className="space-y-0">
                    {cf && [
                      { name: "Net Income", amount: cf.operating?.net_income || 0 },
                      { name: "Accounts Payable (A/P)", amount: cf.operating?.adjustments?.["Accounts Payable (A/P)"] || 0 },
                      { name: "Accounts Receivable (A/R)", amount: cf.operating?.adjustments?.["Accounts Receivable (A/R)"] || 0 },
                      { name: "Depreciation", amount: cf.operating?.adjustments?.["Depreciation"] || 0 },
                      { name: "Net Operating Cash Flow", amount: cf.net_operating || 0 },
                      { name: "Net Investing Cash Flow", amount: cf.net_investing || 0 },
                      { name: "Net Financing Cash Flow", amount: cf.net_financing || 0 },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 gap-2" style={{ borderBottom: "1px solid #F0F1F5" }}>
                        <span className="text-[11px] truncate" style={{ color: "#6B7491" }}>{item.name}</span>
                        <span className="text-[11px] font-bold font-mono flex-shrink-0" style={{ color: item.amount < 0 ? "#F5841F" : "#1C2040" }}>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    {cf && (
                      <div className="flex items-center justify-between py-2.5 mt-1 gap-2" style={{ borderTop: "2px solid #E8EAF0" }}>
                        <span className="text-xs font-bold inline-flex items-center" style={{ color: "#1C2040" }}>
                          Ending Cash
                          <MetricInfoPair metricKey="ending_cash" />
                        </span>
                        <span className="text-xs font-bold font-mono flex-shrink-0" style={{ color: "#1B75BA" }}>{cf.ending_cash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {!cf && <div className="text-[12px] text-center py-6" style={{ color: "#9BA4B8" }}>Loading cash flow...</div>}
                  </div>
                </div>

                {/* Budget vs Actual */}
                <div className="lg:col-span-9 bg-white rounded-xl p-4" style={{ border: "1px solid #E8EAF0" }} data-testid="budget-vs-actual">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-bold" style={{ color: "#1C2040" }}>Budget vs Actual</h4>
                    <DataSourceBadge source="quickbooks" compact className="ml-auto" />
                  </div>
                  <p className="text-[11px] mb-3" style={{ color: "#6B7491" }}>Financial Overview: 2024-2025 vs 2023-2024</p>
                  <div className="overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
                    <div className="flex gap-3" style={{ minWidth: "640px" }}>
                      {/* Income card */}
                      {(() => {
                        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
                        const cyActual = pnl?.monthly_breakdown?.slice(0, 6).map(m => Math.round(m.revenue)) || [10000, 22000, 35000, 60000, 50000, 45000];
                        const cyBudget = cyActual.map(v => Math.round(v * 1.05));
                        const pyActual = cyActual.map(v => Math.round(v * 0.88));
                        const pyBudget = cyActual.map(v => Math.round(v * 0.92));
                        const sum = (a) => a.reduce((s, v) => s + v, 0);
                        const cyB = sum(cyBudget), cyA = sum(cyActual), cyV = cyB - cyA;
                        const pyB = sum(pyBudget), pyA = sum(pyActual), pyV = pyB - pyA;
                        return (
                          <div className="flex-1 rounded-xl p-3" style={{ border: "1px solid #E8EAF0", minWidth: "300px" }} data-testid="budget-income-card">
                            <h5 className="text-sm font-bold mb-2" style={{ color: "#1C2040" }}>Income</h5>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div>
                                <p className="text-[10px] font-semibold mb-1" style={{ color: "#6B7491" }}>Current Year (2024-2025)</p>
                                <div className="flex gap-3">
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Budget</p><p className="text-xs font-bold" style={{ color: "#1C2040" }}>${cyB.toLocaleString()}</p></div>
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Actual</p><p className="text-xs font-bold" style={{ color: "#1C2040" }}>${cyA.toLocaleString()}</p></div>
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Variance</p><p className="text-xs font-bold" style={{ color: cyV < 0 ? "#F5841F" : "#3B82F6" }}>${Math.abs(cyV).toLocaleString()}</p></div>
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] font-semibold mb-1" style={{ color: "#6B7491" }}>Previous Year (2023-2024)</p>
                                <div className="flex gap-3">
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Budget</p><p className="text-xs font-bold" style={{ color: "#1C2040" }}>${pyB.toLocaleString()}</p></div>
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Actual</p><p className="text-xs font-bold" style={{ color: "#1C2040" }}>${pyA.toLocaleString()}</p></div>
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Variance</p><p className="text-xs font-bold" style={{ color: pyV < 0 ? "#F5841F" : "#3B82F6" }}>${Math.abs(pyV).toLocaleString()}</p></div>
                                </div>
                              </div>
                            </div>
                            <Chart type="bar" height={220} options={{
                              chart: { toolbar: { show: false } },
                              plotOptions: { bar: { columnWidth: "75%", borderRadius: 1 } },
                              xaxis: { categories: months, labels: { style: { fontSize: "10px", colors: "#9BA4B8" } } },
                              yaxis: { labels: { style: { fontSize: "9px", colors: "#9BA4B8" }, formatter: v => v.toLocaleString() } },
                              colors: ["#D97706", "#1B75BA", "#FBBF77", "#7DB8E0"],
                              grid: { borderColor: "#F0F1F5", strokeDashArray: 3 },
                              dataLabels: { enabled: false },
                              legend: { position: "bottom", fontSize: "10px", markers: { width: 10, height: 10 } },
                              tooltip: { y: { formatter: v => `$${v.toLocaleString()}` } },
                            }} series={[
                              { name: "Current Year Actual", data: cyActual },
                              { name: "Current Year Budget", data: cyBudget },
                              { name: "Previous Year Actual", data: pyActual },
                              { name: "Previous Year Budget", data: pyBudget },
                            ]} />
                          </div>
                        );
                      })()}

                      {/* Expense card */}
                      {(() => {
                        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
                        const cyActual = pnl?.monthly_breakdown?.slice(0, 6).map(m => Math.round(m.expenses)) || [8000, 16000, 22000, 33000, 28000, 25000];
                        const cyBudget = cyActual.map(v => Math.round(v * 0.95));
                        const pyActual = cyActual.map(v => Math.round(v * 0.92));
                        const pyBudget = cyActual.map(v => Math.round(v * 0.88));
                        const sum = (a) => a.reduce((s, v) => s + v, 0);
                        const cyB = sum(cyBudget), cyA = sum(cyActual), cyV = cyA - cyB;
                        const pyB = sum(pyBudget), pyA = sum(pyActual), pyV = pyA - pyB;
                        return (
                          <div className="flex-1 rounded-xl p-3" style={{ border: "1px solid #E8EAF0", minWidth: "300px" }} data-testid="budget-expense-card">
                            <h5 className="text-sm font-bold mb-2" style={{ color: "#1C2040" }}>Expense</h5>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div>
                                <p className="text-[10px] font-semibold mb-1" style={{ color: "#6B7491" }}>Current Year (2024-2025)</p>
                                <div className="flex gap-3">
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Budget</p><p className="text-xs font-bold" style={{ color: "#1C2040" }}>${cyB.toLocaleString()}</p></div>
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Actual</p><p className="text-xs font-bold" style={{ color: "#1C2040" }}>${cyA.toLocaleString()}</p></div>
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Variance</p><p className="text-xs font-bold" style={{ color: cyV > 0 ? "#F5841F" : "#3B82F6" }}>${Math.abs(cyV).toLocaleString()}</p></div>
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] font-semibold mb-1" style={{ color: "#6B7491" }}>Previous Year (2023-2024)</p>
                                <div className="flex gap-3">
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Budget</p><p className="text-xs font-bold" style={{ color: "#1C2040" }}>${pyB.toLocaleString()}</p></div>
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Actual</p><p className="text-xs font-bold" style={{ color: "#1C2040" }}>${pyA.toLocaleString()}</p></div>
                                  <div><p className="text-[9px]" style={{ color: "#9BA4B8" }}>Variance</p><p className="text-xs font-bold" style={{ color: pyV > 0 ? "#F5841F" : "#3B82F6" }}>${Math.abs(pyV).toLocaleString()}</p></div>
                                </div>
                              </div>
                            </div>
                            <Chart type="bar" height={220} options={{
                              chart: { toolbar: { show: false } },
                              plotOptions: { bar: { columnWidth: "75%", borderRadius: 1 } },
                              xaxis: { categories: months, labels: { style: { fontSize: "10px", colors: "#9BA4B8" } } },
                              yaxis: { labels: { style: { fontSize: "9px", colors: "#9BA4B8" }, formatter: v => v.toLocaleString() } },
                              colors: ["#D97706", "#1B75BA", "#FBBF77", "#7DB8E0"],
                              grid: { borderColor: "#F0F1F5", strokeDashArray: 3 },
                              dataLabels: { enabled: false },
                              legend: { position: "bottom", fontSize: "10px", markers: { width: 10, height: 10 } },
                              tooltip: { y: { formatter: v => `$${v.toLocaleString()}` } },
                            }} series={[
                              { name: "Current Year Actual", data: cyActual },
                              { name: "Current Year Budget", data: cyBudget },
                              { name: "Previous Year Actual", data: pyActual },
                              { name: "Previous Year Budget", data: pyBudget },
                            ]} />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {subTab === "profit_cashflow" && !noFinancialData && <ProfitCashFlowTab summary={summary} mtdSummary={mtdSummary} pnl={pnl} cf={cf} dateRange={dateRange} />}
          {subTab === "income_expenses" && !noFinancialData && <IncomeExpensesTab summary={summary} mtdSummary={mtdSummary} dateRange={dateRange} />}
          {subTab === "liabilities_equity" && !noFinancialData && <LiabilitiesEquityTab summary={summary} mtdSummary={mtdSummary} dateRange={dateRange} />}
          {subTab === "sales" && !noFinancialData && <SalesTab dateRange={dateRange} />}
          {subTab === "expenses_cat" && !noFinancialData && <ExpensesCategoryTab summary={summary} mtdSummary={mtdSummary} dateRange={dateRange} />}
          {subTab === "ratios" && !noFinancialData && <RatiosTab dateRange={dateRange} />}
          {subTab === "conversion" && !noFinancialData && <ConversionCyclesTab dateRange={dateRange} />}
        </div>
      </div>
    </div>
  );
}
